import {useState, useEffect, useCallback, useRef} from 'react';
import {html as beautifyHtml} from 'js-beautify';
import {
    Box,
    Button,
    CircularProgress,
    Chip,
    Collapse,
    Divider,
    Grid,
    makeStyles,
    Paper,
    Snackbar,
    TextField,
    Typography,
} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import DOMPurify from 'dompurify';
import AthenaAPI from '../../APIs/AthenaAPI';
import {AssistContent, AssistContentItem} from '../../APIs/types';

const TABS = [
    {id: 'prequal', label: 'Prequal'},
    {id: 'preapproval', label: 'Pre-Approval'},
    {id: 'quote', label: 'Quotes'},
    {id: 'prosper', label: 'Prosper'},
    {id: 'banking', label: 'Banking'},
    {id: 'policy', label: 'Policy'},
    {id: 'livecall', label: 'Live Call'},
] as const;

type TabId = (typeof TABS)[number]['id'];

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    tabBar: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(3),
    },
    tabButton: {
        textTransform: 'none',
    },
    triggerCard: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
    },
    triggerHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        cursor: 'pointer',
        userSelect: 'none',
        gap: theme.spacing(1),
    },
    triggerMeta: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(0.75),
        flex: 1,
        minWidth: 0,
    },
    triggerMetaRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    triggerDescription: {
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    contentSection: {
        marginTop: theme.spacing(2),
    },
    sectionLabel: {
        marginBottom: theme.spacing(1),
        fontWeight: 600,
    },
    editorRow: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'flex-start',
    },
    editorPane: {
        flex: 1,
        minWidth: 0,
    },
    previewPane: {
        flex: 1,
        minWidth: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1.5),
        minHeight: 108,
        backgroundColor: theme.palette.background.default,
        fontSize: 14,
        lineHeight: 1.6,
        overflowY: 'auto',
        '& ul, & ol': {paddingLeft: theme.spacing(3)},
        '& p': {margin: '0 0 8px'},
        '& strong': {fontWeight: 700},
        '& em': {fontStyle: 'italic'},
    },
    previewLabel: {
        marginBottom: theme.spacing(1),
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    textField: {
        fontFamily: 'monospace',
    },
    saveButton: {
        marginTop: theme.spacing(1),
    },
    divider: {
        margin: theme.spacing(2, 0),
    },
    emptyLabel: {
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },
    inactiveChip: {
        backgroundColor: theme.palette.grey[300],
    },
}));

type ContentDraft = {
    value: string;
    saving: boolean;
    saved: boolean;
};

type DraftMap = Record<string, ContentDraft>;

const HTML_TAG_RE = /<[a-z][\s\S]*?>/i;

function formatHtml(value: string): string {
    if (!HTML_TAG_RE.test(value)) return value;
    try {
        return beautifyHtml(value, {
            indent_size: 4,
            wrap_line_length: 120,
            inline: [],
        });
    } catch {
        return value;
    }
}

function ContentEditor({
    label,
    draftKey,
    item,
    draft,
    onDraftChange,
    onSave,
}: {
    label: string;
    draftKey: string;
    item: AssistContentItem | null;
    draft: ContentDraft | undefined;
    onDraftChange: (key: string, value: string) => void;
    onSave: (key: string) => void;
}) {
    const classes = useStyles();
    const baseline = item?.content ?? '';
    const current = draft?.value ?? baseline;
    const isDirty = current !== baseline;
    const formatPendingRef = useRef(false);

    const handleBlur = useCallback(async () => {
        if (formatPendingRef.current) return;
        formatPendingRef.current = true;
        try {
            const formatted = formatHtml(current);
            if (formatted !== current) onDraftChange(draftKey, formatted);
        } finally {
            formatPendingRef.current = false;
        }
    }, [current, draftKey, onDraftChange]);

    return (
        <Box>
            <Box className={classes.editorRow}>
                <Box className={classes.editorPane}>
                    <Typography variant="body2" className={classes.sectionLabel}>
                        {label}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        variant="outlined"
                        value={current}
                        disabled={draft?.saving}
                        onChange={(e) => onDraftChange(draftKey, e.target.value)}
                        onBlur={handleBlur}
                        inputProps={{className: classes.textField, rowsMin: 4, rowsMax: 25}}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={!isDirty || draft?.saving}
                        className={classes.saveButton}
                        onClick={() => onSave(draftKey)}
                    >
                        {draft?.saving ? 'Saving…' : draft?.saved && !isDirty ? 'Saved' : 'Save'}
                    </Button>
                </Box>
                <Box className={classes.editorPane}>
                    <Typography variant="body2" className={classes.previewLabel}>
                        Preview
                    </Typography>
                    <Box
                        className={classes.previewPane}
                        style={{maxHeight: '65vh', overflowY: 'auto'}}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(current)}}
                    />
                </Box>
            </Box>
        </Box>
    );
}

function TriggerCard({
    trigger,
    drafts,
    onDraftChange,
    onSave,
}: {
    trigger: AssistContent;
    drafts: DraftMap;
    onDraftChange: (key: string, value: string) => void;
    onSave: (key: string) => void;
}) {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);

    const sections: {label: string; item: AssistContentItem | null; draftKey: string}[] = [
        {
            label: 'Script',
            item: trigger.script ?? null,
            draftKey: trigger.script?.id ?? `${trigger.id}:script`,
        },
        {
            label: 'FAQ',
            item: trigger.faq ?? null,
            draftKey: trigger.faq?.id ?? `${trigger.id}:faq`,
        },
        {
            label: 'Tips',
            item: trigger.tips ?? null,
            draftKey: trigger.tips?.id ?? `${trigger.id}:tips`,
        },
    ];

    return (
        <Paper className={classes.triggerCard} variant="outlined">
            <Box className={classes.triggerHeader} onClick={() => setExpanded((v) => !v)}>
                <Box className={classes.triggerMeta}>
                    <Box className={classes.triggerMetaRow}>
                        <Typography variant="subtitle1">
                            <strong>{trigger.title}</strong>
                        </Typography>
                        {trigger.trigger.prequalFlow && (
                            <Chip size="small" label={trigger.trigger.prequalFlow} variant="outlined" />
                        )}
                        {!trigger.active && <Chip size="small" label="inactive" className={classes.inactiveChip} />}
                    </Box>
                    <Typography variant="caption" className={classes.triggerDescription}>
                        {trigger.triggerDescription}
                    </Typography>
                </Box>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            <Collapse in={expanded}>
                <Box className={classes.contentSection}>
                    {sections.map(({label, item, draftKey}, i) => (
                        <Box key={draftKey}>
                            <ContentEditor
                                label={label}
                                draftKey={draftKey}
                                item={item}
                                draft={drafts[draftKey]}
                                onDraftChange={onDraftChange}
                                onSave={onSave}
                            />
                            {i < sections.length - 1 && <Divider className={classes.divider} />}
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Paper>
    );
}

export function AssistContentEditor() {
    const classes = useStyles();
    const [selectedTab, setSelectedTab] = useState<TabId>('prequal');
    const [triggers, setTriggers] = useState<AssistContent[]>([]);
    const [loading, setLoading] = useState(false);
    const [drafts, setDrafts] = useState<DraftMap>({});
    const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({
        open: false,
        message: '',
        severity: 'success',
    });

    const loadTriggers = useCallback(async (tabId: TabId) => {
        setLoading(true);
        setDrafts({});
        try {
            const data = await AthenaAPI.getAssistContentsByTab(tabId);
            const flowOrder = (flow: string | undefined) => (flow === 'single' ? 0 : flow === 'multi' ? 1 : 2);
            const sorted = [...data].sort((a, b) => {
                if (a.priority !== b.priority) return a.priority - b.priority;
                if (tabId === 'prequal') return flowOrder(a.trigger.prequalFlow) - flowOrder(b.trigger.prequalFlow);
                return 0;
            });
            setTriggers(sorted);
        } catch {
            setSnackbar({open: true, message: 'Failed to load triggers.', severity: 'error'});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadTriggers(selectedTab);
    }, [selectedTab, loadTriggers]);

    const handleDraftChange = useCallback((id: string, value: string) => {
        setDrafts((prev) => ({
            ...prev,
            [id]: {...(prev[id] ?? {saving: false, saved: false}), value, saved: false},
        }));
    }, []);

    const handleSave = useCallback(
        async (key: string) => {
            const draft = drafts[key];
            if (!draft) return;

            setDrafts((prev) => ({...prev, [key]: {...prev[key], saving: true}}));
            try {
                if (key.includes(':')) {
                    // New content — key is `${triggerId}:${'script'|'faq'|'tips'}`
                    const colonIdx = key.indexOf(':');
                    const triggerId = key.slice(0, colonIdx);
                    const contentType = key.slice(colonIdx + 1) as 'script' | 'faq' | 'tips';
                    const newItem = await AthenaAPI.createAssistContent(triggerId, contentType, draft.value);
                    setTriggers((prev) =>
                        prev.map((t) =>
                            t.id === triggerId
                                ? {...t, [contentType]: newItem, [`${contentType}ContentId`]: newItem.id}
                                : t,
                        ),
                    );
                    // Remove the temporary draft — the trigger now has a real item with its own ID.
                    setDrafts((prev) => {
                        const next = {...prev};
                        delete next[key];
                        return next;
                    });
                } else {
                    // Existing content — key is the content UUID.
                    await AthenaAPI.updateAssistContent(key, draft.value);
                    setTriggers((prev) =>
                        prev.map((t) => {
                            const updateItem = (item: AssistContentItem | undefined) =>
                                item?.id === key ? {...item, content: draft.value} : item;
                            return {
                                ...t,
                                script: updateItem(t.script),
                                faq: updateItem(t.faq),
                                tips: updateItem(t.tips),
                            };
                        }),
                    );
                    setDrafts((prev) => ({...prev, [key]: {...prev[key], saving: false, saved: true}}));
                }
                setSnackbar({open: true, message: 'Content saved.', severity: 'success'});
            } catch (error: unknown) {
                setDrafts((prev) => ({...prev, [key]: {...prev[key], saving: false}}));
                const message = error instanceof Error ? error.message : 'Failed to save content.';
                setSnackbar({open: true, message, severity: 'error'});
            }
        },
        [drafts],
    );

    return (
        <Box className={classes.root}>
            <Box className={classes.tabBar}>
                {TABS.map((tab) => (
                    <Button
                        key={tab.id}
                        variant={selectedTab === tab.id ? 'contained' : 'outlined'}
                        color={selectedTab === tab.id ? 'primary' : 'default'}
                        className={classes.tabButton}
                        onClick={() => setSelectedTab(tab.id)}
                    >
                        {tab.label}
                    </Button>
                ))}
            </Box>

            {loading ? (
                <Grid container justify="center">
                    <CircularProgress />
                </Grid>
            ) : triggers.length === 0 ? (
                <Typography className={classes.emptyLabel}>No triggers configured for this tab.</Typography>
            ) : (
                triggers.map((trigger) => (
                    <TriggerCard
                        key={trigger.id}
                        trigger={trigger}
                        drafts={drafts}
                        onDraftChange={handleDraftChange}
                        onSave={handleSave}
                    />
                ))
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({...s, open: false}))}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({...s, open: false}))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
